import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import portalApi, { apiError } from './portalApi';
import { T } from './portalTheme';
import { AuthLayout, AuthHeading, Field, TextInput, PasswordInput, Button, Alert, Icon, IC } from './PortalWidgets';

interface InviteContext { sender_org_name: string | null; questionnaire_name: string | null; vendor_name: string | null; is_collaborator?: boolean }

// /questionnaire/portal/register?invite=<token> -- name/email/password, then a "check your
// email" screen, matching the shape of UpGuard's "Set a password" -> "Almost done!" flow.
export default function PortalRegister() {
  const [params] = useSearchParams();
  const invite = params.get('invite') || '';

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [context, setContext] = useState<InviteContext | null>(null);

  useEffect(() => {
    if (!invite) return;
    portalApi.get('/v1/questionnaire-responses/portal/invite-status', { params: { invite } })
      .then(({ data }) => { setEmail(data.recipient_email); setName(data.recipient_name || ''); setContext(data); })
      .catch(() => { /* the form still works with a manually-typed email if this fails */ });
  }, [invite]);

  const mismatch = !!confirmPassword && password !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/register', { invite, email, name, password });
      setRegistered(true);
    } catch (err) {
      setError(apiError(err, 'Could not create your account. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/resend-verification', { email, invite });
    } catch {
      // resend intentionally never surfaces an error -- same non-enumerating behavior as the request itself
    } finally {
      setResending(false);
      setResent(true);
    }
  };

  if (registered) {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center' }}>
          <span style={{ width: 56, height: 56, borderRadius: 16, background: T.blueBg, color: T.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Icon d={IC.mail} size={26} />
          </span>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: -0.3 }}>Check your inbox</div>
          <div style={{ fontSize: 14, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>
            We sent a verification link to <strong style={{ color: T.ink }}>{email}</strong>.
            Click it to confirm your email — you'll be signed in and taken straight to your questionnaires.
          </div>
          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button variant="secondary" full onClick={handleResend} loading={resending} disabled={resent}>
              {resent ? 'Sent again — check your inbox' : "Didn't get it? Resend the email"}
            </Button>
            <Button variant="ghost" full onClick={() => { setRegistered(false); setResent(false); }}>Use a different email</Button>
          </div>
          <div style={{ fontSize: 12.5, color: T.faint, marginTop: 20 }}>The link expires in 24 hours. Check your spam folder if it hasn't arrived.</div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading title="Create your account"
        subtitle="One free account for every security questionnaire sent to your email, from any company using FluxWatch." />
      {context && (
        <div style={{ marginBottom: 20 }}>
          <Alert tone="info" icon={context.is_collaborator ? IC.users : IC.inbox}>
            {context.is_collaborator
              ? <>You've been invited to help answer <strong>{context.questionnaire_name}</strong> for <strong>{context.vendor_name}</strong>.</>
              : <><strong>{context.sender_org_name}</strong> sent you <strong>{context.questionnaire_name}</strong>.</>}
          </Alert>
        </div>
      )}
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Full name">
          <TextInput value={name} onChange={e => setName(e.target.value)} placeholder="Jordan Lee" autoComplete="name" autoFocus />
        </Field>
        <Field label="Work email" hint={invite ? 'This is the address the invitation was sent to.' : undefined}>
          <TextInput value={email} onChange={e => setEmail(e.target.value)} type="email" autoComplete="email" required placeholder="you@company.com" />
        </Field>
        <Field label="Password" hint="At least 8 characters. Longer, with a mix of letters, numbers and symbols, is stronger.">
          <PasswordInput value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" required showStrength />
        </Field>
        <Field label="Confirm password" error={mismatch ? "Passwords don't match." : undefined}>
          <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" full loading={submitting}>{submitting ? 'Creating account…' : 'Create account'}</Button>
      </form>
      <div style={{ fontSize: 13.5, color: T.muted, marginTop: 24, textAlign: 'center' }}>
        Already have an account?{' '}
        <Link to={`/questionnaire/portal/login${invite ? `?invite=${encodeURIComponent(invite)}` : ''}`} className="pw-link" style={{ color: T.primary, fontWeight: 600, textDecoration: 'none' }}>Sign in</Link>
      </div>
    </AuthLayout>
  );
}
